import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Rating,
  Separator,
  Skeleton,
} from '@repo/shared-client/components/ui'
import { hasUserRole } from '@repo/shared-common'
import { useSearch } from '@tanstack/react-router'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { CornerDownRightIcon, EllipsisIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileBanner from 'src/components/primatives/sf-profile-banner/sf-profile-banner'
import { useTranslation } from 'src/lib/intl/react'
import type { DeleteReviewMutation, DeleteReviewMutationVariables } from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'
import ReviewCreateEditDialog from '../review-create-edit-dialog/review-create-edit-dialog'

dayjs.extend(relativeTime)

export const DELETE_REVIEW: TypedDocumentNode<DeleteReviewMutation, DeleteReviewMutationVariables> = gql`
  mutation DeleteReview($reviewId: String!) {
    deleteReview(reviewId: $reviewId) {
      id
      content
      markedDeleted
    }
  }
`

export const ReviewLoadingCard = () => {
  return (
    <div className="flex w-full flex-row items-start gap-1 py-1">
      <Skeleton className="size-8 shrink-0 rounded-full" />
      <div className="flex w-full flex-col items-start gap-1">
        <div className="relative w-full">
          <Skeleton className="h-5 w-full" />
        </div>
      </div>
    </div>
  )
}

interface ReviewProps {
  id: string
  rating: number
  content: string
  createdAt: string
  markedDeleted: boolean
  owner?: {
    id?: string
    name?: string
    profileMedia?: {
      imageUrlSmall?: string
    }
  }
  subReply?: ReviewProps[]
}

interface ReviewCardProps {
  review: ReviewProps
  depth?: number
  profileId?: string
  placeIdOrSlug?: string
  placeOwnerId?: string
  publicationIdOrSlug?: string
  publicationOwnerId?: string
  vendorMarketIdOrSlug?: string
  vendorMarketItemIdOrSlug?: string
  vendorMarketOwnerId?: string
  onReplyReview?: (data: object) => void
  onDeleteReview?: (id: string) => void
  onEditReview?: (data: object) => void
}

const ReviewCard = (props: ReviewCardProps) => {
  const { t } = useTranslation()
  const { depth = 1 } = props

  const [editReview, setEditReview] = useState(false)
  const [replyReview, setReplyReview] = useState(false)
  const { currentUser, hasPermission, isAuthenticated } = useAuth()
  const [reportModal, setReportModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const ratingValue = Math.min(Math.max(0, props?.review.rating), 5.0)
  const search = useSearch({ strict: false })

  const canReplyToReview =
    (!!props.placeIdOrSlug && props.placeOwnerId === currentUser?.profile?.id) ||
    (!!props.publicationIdOrSlug && props.publicationOwnerId === currentUser?.profile?.id) ||
    (!!props.vendorMarketIdOrSlug &&
      !props.vendorMarketItemIdOrSlug &&
      props.vendorMarketOwnerId === currentUser?.profile?.id) ||
    (!!props.vendorMarketItemIdOrSlug && props.vendorMarketOwnerId === currentUser?.profile?.id) ||
    hasUserRole(currentUser?.roles, 'admin')
  const ref = useRef<HTMLDivElement | null>(null)
  let scrollIn = false

  if (search.reviewId && search.reviewId === props?.review.id) {
    scrollIn = true
  }

  useEffect(() => {
    if (scrollIn) {
      ref.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [scrollIn])

  const [deleteReview] = useMutation(DELETE_REVIEW, {
    onCompleted: (data) => {
      toast.success(t('Review Deleted'))

      if (data.deleteReview.markedDeleted === true) {
        props?.onEditReview?.({ id: props?.review.id, content: data.deleteReview.content })
      } else {
        props?.onDeleteReview?.(props?.review.id)
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleDeleteReview = () => {
    setDeleteModal(false)
    deleteReview({ variables: { reviewId: props?.review.id } })
  }

  return (
    <div className="flex w-full flex-col gap-0">
      <Card className={scrollIn ? 'bg-primary/15 ring-primary/40' : undefined} ref={ref} size="sm">
        <CardHeader className="relative">
          <SFProfileBanner
            lowerTitle={
              <div className="flex flex-row items-center justify-start gap-2">
                {depth < 2 ? <Rating size="xs" value={ratingValue} variant="default" /> : null}
                <span className="line-clamp-1 text-muted-foreground text-xs">
                  {dayjs(props?.review.createdAt).fromNow()}
                </span>
              </div>
            }
            profile={props?.review.owner}
            size="sm"
          />
          {!props?.review.markedDeleted && !!isAuthenticated ? (
            <CardAction>
              <DropdownMenu>
                <DropdownMenuTrigger
                  onClick={(e) => e.stopPropagation()}
                  render={
                    <Button
                      aria-label={t('Menu')}
                      className="rounded-full"
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    />
                  }
                >
                  <EllipsisIcon className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {(currentUser?.profile?.id === props?.review.owner?.id || hasPermission('review:update:all')) && (
                    <DropdownMenuItem onClick={() => setEditReview(true)}>{t('Edit')}</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setReportModal(true)}>{t('Report')}</DropdownMenuItem>
                  {(currentUser?.profile?.id === props?.review.owner?.id || hasPermission('review:delete:all')) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeleteModal(true)} variant="destructive">
                        {t('Delete')}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </CardAction>
          ) : null}
        </CardHeader>
        <CardContent>
          <Separator className="w-full" />
          <p className="w-full whitespace-pre-line px-0 py-2">{props?.review.content}</p>
        </CardContent>
        {depth < 2 && isAuthenticated && canReplyToReview ? (
          <>
            <ReviewCreateEditDialog
              onOpenChange={(e) => setReplyReview(e.open)}
              onReviewChange={(data) => {
                props?.onReplyReview?.(data)
              }}
              open={replyReview}
              parentId={props?.review.id}
              placeIdOrSlug={props?.placeIdOrSlug}
              publicationIdOrSlug={props?.publicationIdOrSlug}
              vendorMarketIdOrSlug={props?.vendorMarketIdOrSlug}
              vendorMarketItemIdOrSlug={props?.vendorMarketItemIdOrSlug}
            />
          </>
        ) : null}

        <ConfirmDialog
          onConfirm={handleDeleteReview}
          onOpenChange={(e) => setDeleteModal(e.open)}
          open={deleteModal}
          text={t('Are you sure you want to delete this review?')}
          title={t('Are you sure?')}
          variant="delete"
        />

        <ReviewCreateEditDialog
          onOpenChange={(e) => setEditReview(e.open)}
          onReviewChange={(data) => {
            props?.onEditReview?.(data)
          }}
          open={editReview}
          parentId={depth > 1 ? props?.review.id : undefined}
          review={props?.review}
        />

        <ReportCreate
          onOpenChange={(e) => setReportModal(e.open)}
          open={reportModal}
          targetId={props?.review.id}
          targetKind="REVIEW"
        />
      </Card>

      {isAuthenticated && canReplyToReview ? (
        <div className="flex w-full flex-row justify-end">
          <Button onClick={() => setReplyReview(true)} size="sm" type="button" variant="ghost">
            {t('Reply')}
          </Button>
        </div>
      ) : null}

      <div className="flex w-full flex-col gap-0">
        {props?.review?.subReply?.map((subReview) => {
          return (
            <div className="flex w-full flex-row items-start gap-1" key={subReview.id}>
              <CornerDownRightIcon aria-hidden className="mt-1 size-6 shrink-0 text-muted-foreground" />
              <ReviewCard
                depth={depth + 1}
                onDeleteReview={props?.onDeleteReview}
                onEditReview={props?.onEditReview}
                onReplyReview={props?.onReplyReview}
                placeIdOrSlug={props?.placeIdOrSlug}
                placeOwnerId={props?.placeOwnerId}
                profileId={props?.profileId}
                publicationIdOrSlug={props?.publicationIdOrSlug}
                publicationOwnerId={props?.publicationOwnerId}
                review={subReview}
                vendorMarketIdOrSlug={props?.vendorMarketIdOrSlug}
                vendorMarketItemIdOrSlug={props?.vendorMarketItemIdOrSlug}
                vendorMarketOwnerId={props?.vendorMarketOwnerId}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ReviewCard
